<template>
    <div class="json-tree-item">
        <!-- Collapsible Object/Array -->
        <div v-if="isObject || isArray" class="json-complex">
            <div class="json-header" @click.stop="toggle" :class="{ 'clickable': true }">
                <span class="json-arrow" :class="{ 'collapsed': !isOpen }">▼</span>
                <span v-if="name" class="json-key">{{ name }}: </span>
                <span class="json-type-indicator">{{ isArray ? '[' : '{' }}</span>
                <span v-if="!isOpen" class="json-preview"> {{ preview }} </span>
            </div>

            <div v-if="isOpen" class="json-children">
                <json-tree-item v-for="(val, key) in data" :key="key" :name="isArray ? '' : key" :data="val" />
            </div>

            <div v-if="isOpen" class="json-footer">
                {{ isArray ? ']' : '}' }}<span v-if="!isLast">,</span>
            </div>
            <div v-else class="json-footer-collapsed">
                {{ isArray ? ']' : '}' }}<span v-if="!isLast">,</span>
            </div>
        </div>

        <!-- Primitive Value -->
        <div v-else class="json-primitive">
            <span v-if="name" class="json-key">{{ name }}: </span>
            <span :class="valueClass">{{ formatValue(data) }}</span><span v-if="!isLast">,</span>
        </div>
    </div>
</template>

<script setup>
import { computed, ref } from 'vue';

const props = defineProps({
    data: [Object, Array, String, Number, Boolean, null],
    name: String,
    isLast: {
        type: Boolean,
        default: true
    }
});

const isOpen = ref(true);

const isObject = computed(() => props.data !== null && typeof props.data === 'object' && !Array.isArray(props.data));
const isArray = computed(() => Array.isArray(props.data));

const preview = computed(() => {
    if (isArray.value) return `... ${props.data.length} items ...`;
    if (isObject.value) return `... ${Object.keys(props.data).length} keys ...`;
    return '';
});

const valueClass = computed(() => {
    if (props.data === null) return 'json-null';
    const type = typeof props.data;
    if (type === 'string') return 'json-string';
    if (type === 'number') return 'json-number';
    if (type === 'boolean') return 'json-boolean';
    return '';
});

function formatValue(val) {
    if (val === null) return 'null';
    if (typeof val === 'string') return `"${val}"`;
    return String(val);
}

function toggle() {
    isOpen.value = !isOpen.value;
}
</script>

<style scoped>
.json-tree-item {
    font-family: "Bitstream Vera Sans Mono", "DejaVu Sans Mono", Monaco, Courier, monospace;
    font-size: 12px;
    line-height: 1.5;
}

.json-header {
    cursor: pointer;
    display: flex;
    align-items: center;
}

.json-header:hover {
    background-color: rgba(0, 0, 0, 0.03);
}

.json-arrow {
    display: inline-block;
    width: 16px;
    text-align: center;
    font-size: 10px;
    color: #999;
    transition: transform 0.1s;
    user-select: none;
}

.json-arrow.collapsed {
    transform: rotate(-90deg);
}

.json-key {
    color: #800080;
    /* Purple for keys */
    margin-right: 4px;
}

.json-string {
    color: #2a6e3b;
}

/* Green */
.json-number {
    color: #b85429;
}

/* Orange/Brown */
.json-boolean {
    color: #0000cd;
}

/* Blue */
.json-null {
    color: #808080;
}

/* Gray */

.json-children {
    padding-left: 20px;
    border-left: 1px solid #eee;
    margin-left: 7px;
}

.json-type-indicator {
    color: #444;
}

.json-preview {
    color: #888;
    font-style: italic;
    font-size: 0.9em;
}

.json-primitive {
    padding-left: 16px;
    /* Align with arrow width */
}
</style>
